import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Code2, Shield, Users, AppWindow, ArrowRight, Mail, Lock, Eye, EyeOff, LockKeyhole } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Modal, ModalFooter } from '@/components/ui/modal';
import { LanguageToggle } from '@/components/ui/language-toggle';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/i18n';

type LoginType = 'admin' | 'creator' | null;

export default function MyOrgLandingPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useI18n();
  const [loginType, setLoginType] = useState<LoginType>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      // 根据用户实际角色跳转
      if (result.user.role === 'admin') {
        navigate('/admin');
      } else if (result.user.role === 'creator') {
        navigate('/creator');
      } else {
        navigate('/myorg');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t.landing.loginFailed;
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setLoginType(null);
    setEmail('');
    setPassword('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">
                {t.landing.title} <span className="text-primary-600">{t.landing.titleSuffix}</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <LanguageToggle variant="compact" />
              <Button variant="ghost" size="sm" onClick={() => setLoginType('creator')}>
                {t.landing.creatorLogin}
              </Button>
              <Button size="sm" onClick={() => setLoginType('admin')}>
                {t.landing.adminLogin}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="inline-flex items-center gap-2 bg-primary-50 text-primary-700 px-4 py-2 rounded-full text-sm font-medium">
              <Shield className="w-4 h-4" />
              {t.landing.subtitle}
            </div>
            <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm font-medium">
              <LockKeyhole className="w-4 h-4" />
              {t.landing.privacyBadge}
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            {t.landing.heroTitle1}
            <br />
            <span className="text-primary-600">{t.landing.heroTitle2}</span>
          </h1>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            {t.landing.heroDescription}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" onClick={() => setLoginType('admin')}>
              <Shield className="w-5 h-5 mr-2" />
              {t.landing.adminLogin}
            </Button>
            <Button variant="outline" size="lg" onClick={() => setLoginType('creator')}>
              <Code2 className="w-5 h-5 mr-2" />
              {t.landing.creatorLogin}
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            {t.landing.features}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t.landing.feature1Title}</h3>
              <p className="text-gray-600">{t.landing.feature1Desc}</p>
            </Card>
            <Card className="p-6">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <AppWindow className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t.landing.feature2Title}</h3>
              <p className="text-gray-600">{t.landing.feature2Desc}</p>
            </Card>
            <Card className="p-6">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <LockKeyhole className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t.landing.feature3Title}</h3>
              <p className="text-gray-600">{t.landing.feature3Desc}</p>
            </Card>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            {t.landing.howItWorks}
          </h2>
          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-700 font-bold">1</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{t.landing.step1Title}</h3>
                <p className="text-gray-600">{t.landing.step1Desc}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-700 font-bold">2</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{t.landing.step2Title}</h3>
                <p className="text-gray-600">{t.landing.step2Desc}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-700 font-bold">3</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{t.landing.step3Title}</h3>
                <p className="text-gray-600">{t.landing.step3Desc}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-700 font-bold">4</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{t.landing.step4Title}</h3>
                <p className="text-gray-600">{t.landing.step4Desc}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo accounts */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>{t.landing.demoAccounts}</CardTitle>
              <CardDescription>{t.landing.demoAccountsDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{t.landing.adminAccount}</p>
                    <p className="text-sm text-gray-600">admin@company.com</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setLoginType('admin');
                      setEmail('admin@company.com');
                    }}
                  >
                    {t.common.login} <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{t.landing.creatorAccount}</p>
                    <p className="text-sm text-gray-600">developer@company.com</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setLoginType('creator');
                      setEmail('developer@company.com');
                    }}
                  >
                    {t.common.login} <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-100">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-primary-500 to-primary-700 rounded flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm text-gray-600">{t.landing.copyright}</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <a href="#" className="hover:text-gray-700">{t.landing.helpDocs}</a>
            <a href="#" className="hover:text-gray-700">{t.landing.contactUs}</a>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      <Modal
        isOpen={loginType !== null}
        onClose={closeModal}
        title={loginType === 'admin' ? t.landing.adminLogin : t.landing.creatorLogin}
        description={loginType === 'admin' ? t.landing.loginAdminDesc : t.landing.loginCreatorDesc}
        size="sm"
      >
        <form onSubmit={handleLogin}>
          <div className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="email"
                type="email"
                placeholder={t.landing.enterEmail}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={t.landing.enterPassword}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {error && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
            )}
          </div>
          <ModalFooter>
            <Button type="button" variant="outline" onClick={closeModal}>
              {t.common.cancel}
            </Button>
            <Button type="submit" loading={loading}>
              {t.common.login}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
}
